import React from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import UserProfile from '../../Component/Normal/UserProfile';

import DefaultLogoPath from '../../image/DefaultLogo.svg';
import { ReactComponent as ChainIcon } from '../../image/PartnerChain.svg';
import { ReactComponent as StatsIcon } from '../../image/Chart.svg';
import { ReactComponent as ClassIcon } from '../../image/Class2.svg';
import { ReactComponent as MoreIcon } from '../../image/PartnerMore.svg';

export default function PartnerGymFollowRow(props) {
  const { data, isFollow, onClickRow, onClickMore, popoverContent } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const isConnected = data.status === true;
  const waitingLabel = isFollow
    ? t('myStoreSetting.partnerGym.statusWaitingApply')
    : t('myStoreSetting.partnerGym.statusWaitingApprove');

  // 더보기(...) 버튼 클릭 //
  function onClickMoreButton(e) {
    e.stopPropagation();
    if (onClickMore) onClickMore(data);
  }

  return (
    <RowWrapper hoverBg={theme.primaryAlpha20} onClick={() => onClickRow(data)}>
      <PartnerCell>
        <LogoBox>
          <UserProfile profile={data.storeLogo} defaultProfile={DefaultLogoPath} />
        </LogoBox>
        <TextBlock>
          <NameText color={theme.text}>{data.storeName}</NameText>
          <AddressText color={theme.text}>{data.storeAddress}</AddressText>
        </TextBlock>
      </PartnerCell>
      <StatusCell>
        {
          isConnected &&
          <ChainSvg fillColor={theme.subText} strokeColor={theme.subBackground} />
        }
        <StatusText color={isConnected ? theme.subText : (isFollow ? theme.primary : theme.text)}>
          {isConnected ? t('myStoreSetting.partnerGym.statusConnected') : waitingLabel}
        </StatusText>
      </StatusCell>
      <PermissionCell>
        {
          isFollow && data.isOpenSales &&
          <PermissionChip backgroundColor={theme.buttonFill}>
            <StatsSvg fillColor={theme.text} />
            <PermissionText color={theme.text}>{t('myStoreSetting.partnerGym.permissionStats')}</PermissionText>
          </PermissionChip>
        }
        {
          isFollow && data.isOpenReservation &&
          <PermissionChip backgroundColor={theme.buttonFill}>
            <ClassSvg fillColor={theme.text} detailColor={theme.buttonFill} />
            <PermissionText color={theme.text}>{t('myStoreSetting.partnerGym.permissionClass')}</PermissionText>
          </PermissionChip>
        }
      </PermissionCell>
      <MoreCell onClick={onClickMoreButton}>
        <MoreSvg fillColor={theme.subText} />
        {popoverContent}
      </MoreCell>
    </RowWrapper>
  );
}

const RowWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  min-height: 75px;

  border-radius: 10px;

  transition: background-color 0.2s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.hoverBg};
    }
  }
`;

const PartnerCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  flex: 0 0 520px;
  min-width: 0;

  gap: 21px;

  padding: 0 10px;

  width: 520px;

  overflow: hidden;
`;

const LogoBox = styled.div`
  flex-shrink: 0;

  width: 55px;
  height: 55px;

  border-radius: 5px;

  overflow: hidden;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  flex: 1 1 0;
  max-width: 440px;

  gap: 7.5px;

  padding: 10px 0;
`;

const NameText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;

  max-width: 100%;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const AddressText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;

  max-width: 100%;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const StatusCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  gap: 10px;
`;

const StatusText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const ChainSvg = styled(ChainIcon)`
  flex-shrink: 0;

  width: 27.5px;
  height: 18.75px;

  rect {
    fill: ${props => props.fillColor};
  }

  path {
    stroke: ${props => props.strokeColor};
  }
`;

const PermissionCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  gap: 10px;
`;

const PermissionChip = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;

  height: 28px;
  padding: 0 10px;

  border-radius: 3px;

  background-color: ${props => props.backgroundColor};

  overflow: hidden;
`;

const StatsSvg = styled(StatsIcon)`
  flex-shrink: 0;

  width: 16px;
  height: 16px;

  path {
    fill: ${props => props.fillColor};
  }
`;

const ClassSvg = styled(ClassIcon)`
  flex-shrink: 0;

  width: 16px;
  height: 16px;

  path[fill='#efefef' i] {
    fill: ${props => props.fillColor};
  }

  path[fill='#1d1d1d' i] {
    fill: ${props => props.detailColor};
  }
`;

const PermissionText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const MoreCell = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  flex-shrink: 0;
  align-self: stretch;

  width: 50px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const MoreSvg = styled(MoreIcon)`
  width: 50px;
  height: 14px;

  circle {
    fill: ${props => props.fillColor};
  }
`;
